import { z } from 'zod';
import { suggestionChipsSchema } from './sharing.js';
export const TOOL_NAMES = [
    'set_inputs',
    'calculate',
    'get_values',
    'get_dependencies',
    'run_sensitivity',
    'run_sensitivity_2d',
    'compare_scenarios',
    'goal_seek',
    'validate_input',
    // `generate_excel` REMOVED 2026-05-20: Excel download deprecated platform-wide.
    // In-platform Model tab is the canonical model surface; the API endpoint
    // GET /api/conversations/:id/excel returns HTTP 410 DOWNLOAD_DISABLED.
    'export_sensitivity_tab',
    'get_model_info',
    'set_table_inputs',
    'market_data_enrichment',
    'apply_market_defaults',
    'accept_defaults',
    'propose_plan',
    'propose_table_fill',
    'set_property_address',
];
export const toolNameSchema = z.enum(TOOL_NAMES);
// --- SSE event discriminated union ---
export const sseTokenEventSchema = z.object({
    type: z.literal('token'),
    content: z.string(),
});
export const sseToolStartEventSchema = z.object({
    type: z.literal('tool_start'),
    tool: toolNameSchema,
});
/**
 * Tool result SSE event. Emitted by the API's LangGraph stream adapter
 * after a tool completes execution.
 *
 * `editId` (shape landed in realtime-collab-foundation Phase 1; producer
 * wired in Phase 2 Session 1, 2026-04) carries the UUIDv7 minted by
 * `newEditId()` and round-tripped through `recordEdit()` for tools that
 * mutate state. Non-mutating tools (get_values, calculate, get_dependencies,
 * etc.) omit the field; the producer (`mapStreamEventToSSE`) lifts it onto
 * the event ONLY when the tool result JSON carries a non-empty string
 * `editId` — wrong-type or `null` values are filtered. Zod's `.uuid()`
 * validator accepts all UUID versions (v4 and v7), so no schema change is
 * needed when we start producing v7 values in Phase 2.
 *
 * Clients use `editId` to dedupe against the Realtime DiffOp broadcast that
 * will arrive on the conversation channel for the same state change — the
 * two transports deliver the same edit from different angles, and the
 * client-side dedupe set (bounded LRU in use-conversation-channel) prevents
 * double-application. Additive to the wire shape — existing consumers
 * ignore the new field.
 */
export const sseToolResultEventSchema = z.object({
    type: z.literal('tool_result'),
    tool: toolNameSchema,
    result: z.any(),
    editId: z.string().uuid().nullable().optional(),
});
export const sseDoneEventSchema = z.object({
    type: z.literal('done'),
    state: z.any(),
    /**
     * Recomputed suggestion chips, emitted after a public-viewer AI turn.
     * Additive optional field — existing `done` payloads without chips still parse.
     * When present, carries exactly 3 chips (`.length(3)`) per the cross-domain SSOT
     * in `suggestionChipsSchema` (public-deal-link-polish WS2, ADR 0003).
     * Web consumers read this in the `done` case of `useSharedStreaming` and
     * swap the displayed chip set; turn-0 seed stays `usePublicDeal().data.suggestionChips`.
     * Strictly additive — no exhaustive-switch breakage; no new SSE variant minted (ADR 0003).
     */
    suggestionChips: suggestionChipsSchema.optional(),
});
/**
 * Structured details on an SSE error event. Currently only populated when
 * `code === 'MODEL_VERSION_REMOVED'` so the web layer can render the
 * missing-model recovery panel inline without an extra round-trip.
 *
 * `latestAvailable` is null when no other version exists for the modelId —
 * web uses null to switch to the destructive "Delete this conversation"
 * branch (see `model-registry-cleanup` Phase 2 Session 12).
 */
export const sseErrorDetailsSchema = z.object({
    modelId: z.string().optional(),
    modelVersion: z.string().optional(),
    latestAvailable: z.string().nullable().optional(),
    // signup-billing-mvp Phase 2 Session 5 (2026-05-13) — populated when
    // `code: 'PAYMENT_REQUIRED'` is emitted by `streamMessage`'s eligibility
    // gate. Mirrors `RunEligibilityReason` from `billing.types.ts`; declared
    // here as a string to avoid the cross-module dependency (the values are
    // canonical: 'locked' | 'no_subscription' | 'allowance_exhausted').
    reason: z.string().optional(),
});
/**
 * Canonical SSE error codes.
 *
 * Stream-terminating errors carrying `code` field on `SSEEvent { type: 'error' }`.
 * Distinct from `tool_error` (recoverable; per agent-tool-reliability — see
 * `tool-results.types.ts:toolErrorSchema`).
 *
 * Known codes (the schema is permissive on `code: z.string()` so additions
 * don't require a schema bump, but keep this list authoritative):
 * - `MODEL_VERSION_REMOVED` — model_registry version no longer exists (model-registry-cleanup)
 * - `MODEL_NOT_FOUND` — model_id not in registry (model-registry-cleanup)
 * - `INCOMPATIBLE_VERSION` — additive-only contract violation on re-ingest (model-registry-cleanup)
 * - `RATES_UNAVAILABLE` — FRED key missing (market-rates-live)
 * - `COMPUTATION_TIMEOUT` — wall-clock budget exceeded for a computation
 *   (operating-asset-e2e-validation Phase 1 Session 3, 2026-04-29). Emitted
 *   when the API's `streamMessage` exceeds `COMPUTATION_BUDGET_MS` (default
 *   60s) — terminating; client retries on user action. Distinct from the
 *   recoverable `COMPUTATION_TIMEOUT` ToolError code (post-hoc detection
 *   inside a single tool call; the agent can retry).
 * - `CONVERSATION_BUSY` — concurrent stream rejected. Emitted when a second
 *   `streamMessage` enters with an active run already in the in-process
 *   RunRegistry for the same conversation (agent-ux-optimization Phase 1
 *   Session 6, 2026-05-06). Terminating; client should abort the prior
 *   stream OR wait for it to complete before retrying.
 * - `PAYMENT_REQUIRED` — pre-run eligibility gate rejected the run
 *   (signup-billing-mvp Phase 2, 2026-05-13). Emitted by `streamMessage`
 *   when `check_run_eligibility(user_id)` returns `{ok:false}` for a
 *   non-admin caller (allowance exhausted / no subscription / locked).
 *   Terminating; the sync `sendMessage` route maps it to HTTP 402. The
 *   `details.reason` field carries the `RunEligibilityReason`. Client
 *   surfaces the upgrade / allowance-reset copy; retries only on user action.
 */
export const SSE_ERROR_CODES = {
    MODEL_VERSION_REMOVED: 'MODEL_VERSION_REMOVED',
    MODEL_NOT_FOUND: 'MODEL_NOT_FOUND',
    INCOMPATIBLE_VERSION: 'INCOMPATIBLE_VERSION',
    RATES_UNAVAILABLE: 'RATES_UNAVAILABLE',
    COMPUTATION_TIMEOUT: 'COMPUTATION_TIMEOUT',
    CONVERSATION_BUSY: 'CONVERSATION_BUSY',
    PAYMENT_REQUIRED: 'PAYMENT_REQUIRED',
    /**
     * Upstream LLM provider returned `overloaded_error` mid-stream and we
     * exhausted our retry budget. `retriable: true` on the SSE event tells the
     * web client to render a friendly retry card instead of the generic error
     * surface — the underlying issue is transient capacity, not a code failure.
     * See conversations.service.v2.ts overload-retry loop.
     */
    UPSTREAM_OVERLOADED: 'UPSTREAM_OVERLOADED',
};
export const sseErrorEventSchema = z.object({
    type: z.literal('error'),
    message: z.string(),
    code: z.string(),
    details: sseErrorDetailsSchema.optional(),
    /**
     * When `true`, the web client renders a friendly retry card with a Retry
     * button instead of the generic error banner. Reserved for UPSTREAM_OVERLOADED
     * after retries exhausted; other errors leave this `undefined` (rendered as
     * non-retriable).
     */
    retriable: z.boolean().optional(),
});
/**
 * Visible retry indicator. Emitted by the API SSE controller between retry
 * attempts when an upstream `overloaded_error` is being retried with backoff.
 * Web consumers render an inline "Retrying… (attempt N/total)" status under
 * the chat input so the user understands the 1-7s wait isn't a hang.
 *
 * Non-terminating — followed by either a successful stream (token/tool_start/...)
 * or, after the final retry, a terminating `error` event with
 * `code: 'UPSTREAM_OVERLOADED', retriable: true`.
 */
export const sseRetryStatusEventSchema = z.object({
    type: z.literal('retry_status'),
    attempt: z.number().int().positive(),
    total_attempts: z.number().int().positive(),
    reason: z.enum(['service_busy']),
    /** Backoff delay in milliseconds before the next attempt fires. */
    delay_ms: z.number().int().nonnegative(),
});
/**
 * Tool-level failure event. Emitted by the API streaming adapter when a tool
 * returns an `errors[]` result (see `toolErrorSchema` in
 * `tool-results.types.ts`) or when the tool invocation itself throws.
 * Distinct from the stream-terminating `error` event above — a `tool_error`
 * does NOT end the stream; the agent continues and can retry on its next
 * turn. Web consumers render it as an inline callout in the chat thread
 * (see ChatThread.tsx — populated in Phase 4).
 *
 * `details` carries the full `ToolError` payload (validInputs / acceptable
 * range / zodIssues / etc.) so the callout can expand to show what the
 * agent was told.
 */
export const sseToolErrorEventSchema = z.object({
    type: z.literal('tool_error'),
    tool: toolNameSchema,
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
});
/**
 * Security filter — terminating event. Emitted by the API SSE controller
 * when the input filter (`runInputFilter`) returns `verdict: 'block'`
 * before the agent is invoked. The agent never runs; the stream ends
 * immediately. Web client renders an inline `<SecurityNotice variant='block'>`
 * banner with the generic reason string and treats the stream as terminated
 * (no `done` event will follow).
 *
 * Distinct from the recoverable `security_redact` variant below — `security_block`
 * IS terminating, like `error`. Distinct from `error` itself in that it carries
 * `signals[]` describing which input-filter pattern fired (system_prompt_extraction,
 * instruction_override, etc.) for forensic purposes; the user-facing `reason` is
 * intentionally generic so the response doesn't disclose detection logic.
 *
 * Ships with prompt-langgraph-optimization Phase 5 Session 2 (2026-05-03).
 */
export const SECURITY_REDACT_CATEGORIES = [
    // Stage 1 (per-token regex) categories — match TokenFilterCategory in agents/lib/security/output-filter.ts
    'system_prompt',
    'tool_name',
    'cross_user_id',
    // Stage 2 (post-stream Haiku) categories — match SemanticFilterCategory
    'methodology',
    'calibration_band',
    'semantic_rule',
];
export const securityBlockEventSchema = z.object({
    type: z.literal('security_block'),
    /** Generic user-facing message; intentionally vague (does NOT disclose detection logic). */
    reason: z.string(),
    /** Identifiers of which input-filter patterns fired. Forensic detail; not user-facing. */
    signals: z.array(z.string()),
});
/**
 * Security filter — non-terminating redaction event. Emitted by the API SSE
 * controller in two stages:
 *
 * - `stage: 'token'` — Stage 1 inline per-token regex caught a deterministic
 *   leak pattern (system_prompt fragment, tool name, cross-user UUID). The
 *   `redacted_span` is the original token; the `replacement` is what
 *   shipped down the wire INSTEAD of the original. Web client surfaces
 *   the redaction as inline strikethrough or replacement note.
 *
 * - `stage: 'semantic'` — Stage 2 post-stream Haiku semantic check caught
 *   a methodology / calibration-band / semantic-rule disclosure AFTER the
 *   stream's content already shipped. Emitted BEFORE the `done` event. Web
 *   client renders a post-message redaction footer pointing to the offending
 *   span.
 *
 * Stream continues after a `security_redact` event — distinct from the
 * terminating `security_block` and `error` variants.
 *
 * Ships with prompt-langgraph-optimization Phase 5 Session 2 (2026-05-03).
 */
export const securityRedactEventSchema = z.object({
    type: z.literal('security_redact'),
    category: z.enum(SECURITY_REDACT_CATEGORIES),
    stage: z.enum(['token', 'semantic']),
    /** The original span that was redacted. Always present for both stages. */
    redacted_span: z.string(),
    /** What the API shipped (Stage 1) or what the frontend should render in place of the span (Stage 2). */
    replacement: z.string(),
});
// ---------------------------------------------------------------------------
// agent-ux-optimization Phase 1 — 12 new SSE variants (additive)
// ---------------------------------------------------------------------------
// ---------- Reasoning lifecycle (3 variants) ----------
/**
 * Adaptive thinking ("extended thinking" / Anthropic native) — start of a
 * reasoning content block. `content_block_index` is the Anthropic-native index
 * we use as a stream-local join key for delta and stop events. Reasoning is
 * conversational-agent only; the solver path does NOT emit thinking_*.
 */
export const sseThinkingStartEventSchema = z.object({
    type: z.literal('thinking_start'),
    content_block_index: z.number(),
});
export const sseThinkingDeltaEventSchema = z.object({
    type: z.literal('thinking_delta'),
    content_block_index: z.number(),
    text: z.string(),
});
export const sseThinkingStopEventSchema = z.object({
    type: z.literal('thinking_stop'),
    content_block_index: z.number(),
    duration_ms: z.number(),
    token_count: z.number().optional(),
});
// ---------- Tool input lifecycle (3 variants) ----------
/**
 * Tool input lifecycle — emitted as the agent constructs a tool call. Pairs
 * with tool_input_delta + tool_input_ready via `tool_call_id`. The mapping
 * from Anthropic's `content_block_index` to `tool_call_id` is reconciled in
 * the API streaming pipeline (see Phase 1 Session 5).
 */
export const sseToolInputStartEventSchema = z.object({
    type: z.literal('tool_input_start'),
    tool: toolNameSchema,
    tool_call_id: z.string(),
});
export const sseToolInputDeltaEventSchema = z.object({
    type: z.literal('tool_input_delta'),
    tool_call_id: z.string(),
    /** Raw, possibly-truncated JSON fragment as the model streams the tool call args. */
    partial_input_json: z.string(),
});
export const sseToolInputReadyEventSchema = z.object({
    type: z.literal('tool_input_ready'),
    tool_call_id: z.string(),
    /** Fully-parsed and Zod-validated args. */
    input: z.record(z.string(), z.unknown()),
});
// ---------- Tool internal progress (1 variant) ----------
/**
 * Per-tool progress emission. Five tools emit progress events today:
 * run_sensitivity_2d, market_data_enrichment, generate_excel, run_sensitivity,
 * apply_market_defaults. Payload shape is tool-specific and validated by the
 * consumer against the formal Zod schemas in `tool-progress.ts`.
 */
export const sseToolProgressEventSchema = z.object({
    type: z.literal('tool_progress'),
    tool_call_id: z.string(),
    tool: toolNameSchema,
    /** Tool-specific phase discriminator, e.g. 'cell_computed' for run_sensitivity_2d. */
    phase: z.string(),
    payload: z.record(z.string(), z.unknown()),
});
// ---------- Plan-then-execute (3 variants) ----------
/**
 * One step of an agent-proposed plan. Steps either name a tool the agent
 * intends to call (`tool` field set) or describe prose-only reasoning steps
 * (`tool` omitted). Emitted via the `propose_plan` synthetic tool.
 */
export const planStepSchema = z.object({
    step_id: z.string(),
    label: z.string(),
    tool: toolNameSchema.optional(),
});
export const ssePlanProposedEventSchema = z.object({
    type: z.literal('plan_proposed'),
    steps: z.array(planStepSchema).min(1),
});
export const ssePlanStepActiveEventSchema = z.object({
    type: z.literal('plan_step_active'),
    step_id: z.string(),
});
export const ssePlanStepCompleteEventSchema = z.object({
    type: z.literal('plan_step_complete'),
    step_id: z.string(),
});
// ---------- Abort (1 variant) ----------
/**
 * Stream aborted before the normal `done` event. `partial: true` indicates
 * tool calls landed before the abort; `false` means the abort hit before any
 * tool execution. Reasons distinguish user-initiated aborts (Stop button)
 * from server-side timeouts and budget exhaustion.
 */
export const sseAbortedEventSchema = z.object({
    type: z.literal('aborted'),
    reason: z.enum(['user', 'timeout', 'budget', 'error']),
    partial: z.boolean(),
});
// ---------- End-of-turn summary (1 variant) ----------
/**
 * Structured end-of-turn summary section. Replaces ad-hoc markdown summaries
 * the agent used to inline in prose. The web layer renders sections as a
 * collapsed-by-default `<SummaryCard>`. Q1 decision (locked 2026-05-06):
 * always default-collapsed.
 */
export const summarySectionSchema = z.object({
    heading: z.string(),
    body: z.string(),
});
export const sseSummaryEventSchema = z.object({
    type: z.literal('summary'),
    sections: z.array(summarySectionSchema),
});
/**
 * Protocol-version handshake — terminating event. Emitted by the API SSE
 * controller when a v1 client (missing or wrong `X-Arcanum-Protocol` header)
 * connects after Phase 4 deploys. Stream closes; web's stream handler hard-
 * reloads `window.location` once. After reload, the new bundle sends
 * `X-Arcanum-Protocol: v2` and connects normally.
 *
 * Distinct from `sseErrorEvent` — protocol_changed is its own event variant
 * so the client doesn't need to discriminate on `code` field. Ships with
 * `builder-ssot-consolidation` Phase 4.
 */
export const sseProtocolChangedEventSchema = z.object({
    type: z.literal('protocol_changed'),
    requiredVersion: z.literal('v2'),
});
// ---------- Discriminated union (extended 8 -> 20) ----------
export const sseEventSchema = z.discriminatedUnion('type', [
    // Existing 8 variants (unchanged)
    sseTokenEventSchema,
    sseToolStartEventSchema,
    sseToolResultEventSchema,
    sseToolErrorEventSchema,
    sseDoneEventSchema,
    sseErrorEventSchema,
    securityBlockEventSchema,
    securityRedactEventSchema,
    // New 12 variants (all additive — agent-ux-optimization Phase 1)
    sseThinkingStartEventSchema,
    sseThinkingDeltaEventSchema,
    sseThinkingStopEventSchema,
    sseToolInputStartEventSchema,
    sseToolInputDeltaEventSchema,
    sseToolInputReadyEventSchema,
    sseToolProgressEventSchema,
    ssePlanProposedEventSchema,
    ssePlanStepActiveEventSchema,
    ssePlanStepCompleteEventSchema,
    sseAbortedEventSchema,
    sseSummaryEventSchema,
    // Phase 7 — Anthropic overload retry (2026-05-15). Visible retry indicator
    // between attempts; followed by either successful stream or terminating
    // `error` with code=UPSTREAM_OVERLOADED retriable=true.
    sseRetryStatusEventSchema, // 21
    // builder-ssot-consolidation Phase 4 — protocol-version handshake.
    sseProtocolChangedEventSchema, // 22
]);
